import React, { useState, useRef, useCallback } from "react";
import { Button, Menu, MenuItem, ListItemIcon, ListItemText, Tooltip } from "@mui/material";
import ShareIcon from "@mui/icons-material/Share";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailIcon from "@mui/icons-material/Email";
import { createShareLink, OgMeta } from "../../utils/comparatorApi";

interface Props {
  url?: string;
  title?: string;
  label?: string;
  size?: "small" | "medium" | "large";
  og?: OgMeta;
  onShare?: () => void;
}

const ShareButton: React.FC<Props> = ({ url, title, label = "Share", size = "small", og, onShare }) => {
  const [busy, setBusy] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [shortUrl, setShortUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleClick = useCallback(async () => {
    setBusy(true);
    try {
      const result = await createShareLink(url || window.location.href, og);
      onShare?.();
      if (navigator.share) {
        await navigator.share({ title: title || document.title, url: result.url });
      } else {
        setShortUrl(result.url);
        setAnchorEl(buttonRef.current);
      }
    } catch {
      // AbortError on user cancel is normal — ignore silently
    } finally {
      setBusy(false);
    }
  }, [url, title, og, onShare]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shortUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch {}
    setAnchorEl(null);
  };

  return (
    <>
      <Tooltip title={copied ? "Copied!" : ""} open={copied} arrow>
        <Button
          ref={buttonRef}
          size={size}
          variant="contained"
          startIcon={<ShareIcon />}
          onClick={handleClick}
          disabled={busy}
          sx={{
            borderRadius: "20px",
            bgcolor: "#1a1a2e",
            color: "#fff",
            alignItems: "center",
            "&:hover": { bgcolor: "#2e2e4e" },
            "&:disabled": { bgcolor: "rgba(0,0,0,0.12)" },
            "& .MuiButton-startIcon": { display: "flex", alignItems: "center", marginTop: 0, marginBottom: 0 },
            "& .MuiButton-startIcon svg": { display: "block" },
          }}
        >
          {label}
        </Button>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        transformOrigin={{ horizontal: "left", vertical: "top" }}
        anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
      >
        <MenuItem onClick={handleCopy}>
          <ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Copy link</ListItemText>
        </MenuItem>
        <MenuItem
          component="a"
          href={`https://wa.me/?text=${encodeURIComponent(shortUrl)}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => setAnchorEl(null)}
        >
          <ListItemIcon><WhatsAppIcon fontSize="small" sx={{ color: "#25D366" }} /></ListItemIcon>
          <ListItemText>WhatsApp</ListItemText>
        </MenuItem>
        <MenuItem
          component="a"
          href={`mailto:?subject=${encodeURIComponent(title || "Charter boat")}&body=${encodeURIComponent(shortUrl)}`}
          onClick={() => setAnchorEl(null)}
        >
          <ListItemIcon><EmailIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Email</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export default ShareButton;
